import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { botServices } from './ServicesSection';

interface CalculatorSectionProps {
  selectedServiceId: string | null;
  onOrder: (serviceId: string, price: number) => void;
}

// Multipliers calibrated against agency market 2026: Standard 1.7× reflects
// real time cost of CRM/payments/admin add-ons; Advanced 3.0× matches PAS7
// "Integrated business bots 2-3× MVP" tier (Mini App / multi-integration /
// custom UI / RAG-grade AI / multilingual).
const complexityMultipliers: Record<string, number> = {
  Basic: 1,
  Standard: 1.7,
  Advanced: 3.0,
};

const complexityDelivery: Record<string, { min: number; max: number }> = {
  Basic: { min: 3, max: 7 },
  Standard: { min: 6, max: 12 },
  Advanced: { min: 12, max: 24 },
};

const featureIds = [
  'ai',
  'payments',
  'dashboard',
  'crm',
  'multilang',
  'analytics',
  'api',
  'design',
] as const;

// Add-on pricing in ₽ — STARTER-mode (first 5–10 orders, ~50% of full
// margin model). AI = must-have premium (8h dev), Dashboard = biggest
// line (React admin, 14h), Design = separate Figma + UI-kit (12h).
// Raise alongside base prices once portfolio is built.
const featurePrices: Record<string, number> = {
  ai: 10000,
  payments: 5000,
  dashboard: 18000,
  crm: 10000,
  multilang: 8000,
  analytics: 8000,
  api: 10000,
  design: 15000,
};

export default function CalculatorSection({
  selectedServiceId,
  onOrder,
}: CalculatorSectionProps) {
  const { t } = useTranslation();
  const [selectedBotIndex, setSelectedBotIndex] = useState(0);
  const [complexity, setComplexity] = useState<'Basic' | 'Standard' | 'Advanced'>('Standard');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [expressDelivery, setExpressDelivery] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedServiceId) return;
    const idx = botServices.findIndex((s) => s.id === selectedServiceId);
    if (idx >= 0) setSelectedBotIndex(idx);
  }, [selectedServiceId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const bot = botServices[selectedBotIndex];
  const toggleFeature = (id: string) =>
    setSelectedFeatures((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );

  const breakdown = useMemo(() => {
    const basePrice = (bot.priceMin + bot.priceMax) / 2;
    const multiplier = complexityMultipliers[complexity];
    const base = Math.round(basePrice * multiplier);
    const featuresCost = selectedFeatures.reduce(
      (sum, fid) => sum + (featurePrices[fid] || 0),
      0
    );
    const subtotal = base + featuresCost;
    const expressAdd = expressDelivery ? Math.round(subtotal * 0.3) : 0;
    return { base, featuresCost, expressAdd, total: subtotal + expressAdd };
  }, [bot, complexity, selectedFeatures, expressDelivery]);

  const delivery = complexityDelivery[complexity];
  const deliveryDays = expressDelivery
    ? `${Math.max(1, Math.round(delivery.min * 0.5))}–${Math.max(2, Math.round(delivery.max * 0.5))}`
    : `${delivery.min}–${delivery.max}`;

  return (
    <section ref={sectionRef} id="calculator" className="py-12 sm:py-16 lg:py-20 px-5 scroll-mt-16">
      <div className="max-w-lg sm:max-w-2xl lg:max-w-3xl mx-auto">
        <div className="reveal opacity-0 mb-6 lg:mb-10 lg:text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <span className="w-1 h-1 rounded-full bg-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-[0.12em]">
              {t('calculator.label')}
            </p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">
            {t('calculator.title')}
          </h2>
        </div>

        <div className="reveal opacity-0 glass-card rounded-2xl p-5 sm:p-6 lg:p-8">
          {/* Bot Type as chips */}
          <div className="mb-5">
            <label className="text-[#8A93A7] text-[11px] font-medium mb-2 block uppercase tracking-wider">
              {t('calculator.botType')}
            </label>
            <div className="flex flex-wrap gap-2">
              {botServices.map((service, idx) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => setSelectedBotIndex(idx)}
                  className={`text-[12px] px-3 py-1.5 rounded-full transition-all border
                    ${
                      selectedBotIndex === idx
                        ? 'bg-[#00D4FF] text-[#0B0E14] border-[#00D4FF] font-semibold shadow-md shadow-[rgba(0,212,255,0.25)]'
                        : 'border-[rgba(255,255,255,0.08)] text-[#B5BBC9] hover:text-[#F0F2F5]'
                    }`}
                >
                  {t(`services.bots.${service.id}.name`)}
                </button>
              ))}
            </div>
          </div>

          {/* Complexity */}
          <div className="mb-5">
            <label className="text-[#8A93A7] text-[11px] font-medium mb-2 block uppercase tracking-wider">
              {t('calculator.complexity')}
            </label>
            <div className="glass-input rounded-xl p-1 flex">
              {(['Basic', 'Standard', 'Advanced'] as const).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setComplexity(level)}
                  className={`flex-1 h-10 rounded-lg text-xs font-semibold transition-all
                    ${
                      complexity === level
                        ? 'bg-[#00D4FF] text-[#0B0E14] shadow-lg shadow-[rgba(0,212,255,0.25)]'
                        : 'text-[#B5BBC9] hover:text-[#F0F2F5]'
                    }`}
                >
                  {t(`calculator.${level.toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-5">
            <label className="text-[#8A93A7] text-[11px] font-medium mb-2 block uppercase tracking-wider">
              {t('calculator.features')}
            </label>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {featureIds.map((fid) => {
                const isChecked = selectedFeatures.includes(fid);
                return (
                  <button
                    key={fid}
                    type="button"
                    onClick={() => toggleFeature(fid)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all duration-250 text-left
                      ${
                        isChecked
                          ? 'border-[rgba(0,212,255,0.4)] bg-[rgba(0,212,255,0.06)]'
                          : 'border-[rgba(255,255,255,0.06)] hover:border-[rgba(0,212,255,0.2)]'
                      }`}
                  >
                    <div
                      className={`w-[18px] h-[18px] rounded-[5px] flex items-center justify-center flex-shrink-0 transition-all
                        ${isChecked ? 'bg-[#00D4FF]' : 'border border-[#8A93A7] bg-transparent'}`}
                    >
                      {isChecked && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            stroke="#0B0E14"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#F0F2F5] text-[12px] leading-tight">
                        {t(`calculator.featuresList.${fid}`)}
                      </p>
                      <p className="text-[#00D4FF] text-[11px]">+{featurePrices[fid].toLocaleString('ru-RU')} ₽</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6 py-3 border-t border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setExpressDelivery(!expressDelivery)}
                className={`ios-toggle ${expressDelivery ? 'active' : ''}`}
                aria-label="Express delivery"
              />
              <span className="text-[#F0F2F5] text-sm font-medium">
                {t('calculator.expressDelivery')}
              </span>
            </div>
            <span className="text-[#FFB020] text-xs font-bold bg-[rgba(255,176,32,0.12)] px-2 py-0.5 rounded-full">
              {t('calculator.expressExtra')}
            </span>
          </div>

          {/* Breakdown + Price */}
          <div className="mb-5 py-5 px-5 glass rounded-2xl border border-[rgba(0,212,255,0.1)]">
            <p className="text-[#8A93A7] text-[10px] uppercase tracking-wider mb-3 text-center">
              {t('calculator.priceLabel')}
            </p>
            <p className="text-gradient text-[42px] font-extrabold leading-none text-center">
              от {(Math.floor(breakdown.total / 5000) * 5000).toLocaleString('ru-RU')} ₽
            </p>
            <p className="text-[#B5BBC9] text-xs mt-2 text-center">
              {t('calculator.delivery')}: {deliveryDays} {t('calculator.days')}
            </p>
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)] space-y-1 text-[11px]">
              <BreakdownRow
                label={t('calculator.botType')}
                value={`${breakdown.base.toLocaleString('ru-RU')} ₽`}
              />
              {breakdown.featuresCost > 0 && (
                <BreakdownRow
                  label={t('calculator.features')}
                  value={`+${breakdown.featuresCost.toLocaleString('ru-RU')} ₽`}
                />
              )}
              {breakdown.expressAdd > 0 && (
                <BreakdownRow
                  label={t('calculator.expressDelivery')}
                  value={`+${breakdown.expressAdd.toLocaleString('ru-RU')} ₽`}
                />
              )}
            </div>
          </div>

          <p className="text-[#8A93A7] text-[11px] leading-relaxed text-center mb-4">
            {t('calculator.disclaimer')}
          </p>

          <button
            type="button"
            onClick={() => onOrder(bot.id, breakdown.total)}
            className="w-full h-12 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl py-3.5"
          >
            {t('calculator.orderBtn')}
          </button>
        </div>
      </div>
    </section>
  );
}

function BreakdownRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#B5BBC9]">{label}</span>
      <span className="text-[#F0F2F5] font-medium">{value}</span>
    </div>
  );
}
